import React, { useEffect, useState, useMemo, FC, useContext } from 'react';
import { Box, Button, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import MoodBarometerIntro from './MoodBarometerIntro';
import MoodBarometerComment from './MoodBarometerComment';
import SurveyService from '../../services/SurveyService';
import { constants } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';
import SurveyUtils from '../../shared/SurveyUtils';
import Autolink from '../Autolink/Autolink';

interface Props {
  id: string;
  primaryColor: string;
  secondaryColor: string;
  customFont: string;
  companyName: string;
  feedbackActive?: boolean | undefined;
  feedbackText?: string;
  feedbackTitle?: string;
  introText?: string;
  outroText?: string;
  negativeText?: string;
  positiveText?: string;
  neutralText?: string;
  title?: string;
  platform?: string;
  platformUrl?: string;
  platformName?: string;
  contactEmail?: string;
  question?: string;
}

const MoodBarometerContent: FC<Props> = (props) => {
  const {
    id,
    primaryColor,
    secondaryColor,
    customFont,
    companyName,
    title,
    feedbackActive,
    feedbackText,
    feedbackTitle,
    introText,
    outroText,
    negativeText,
    positiveText,
    neutralText,
    platform,
    platformUrl,
    platformName,
    contactEmail,
    question,
  } = props;

  const theme = useTheme();
  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation();
  const t = localContext ? localContext.t : globalT;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [currentPlatform, setCurrentPlatform] = useState<string>(platform || '');
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedMood, setSelectedMood] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const [lastAnswerId, setLastAnswerId] = useState<string>('');

  const isLastPage = currentPage === 2;
  const isFirstPage = currentPage === 0;

  useEffect(() => {
    if (isLastPage && feedbackActive && !isFeedbackSubmitted) {
      setIsFeedbackSubmitted(false);
    }
  }, [currentPage, feedbackActive, isFeedbackSubmitted]);

  const formatTextToString = (text: any) => {
    if (!text) return null;

    return text.replace(/(\r\n|\r|\n)/g, ' ');
  };

  const handlePageChange = (change: number) => setCurrentPage((prev) => prev + change);
  const handleSaveMood = async () => {
    setIsFeedbackSubmitted(true);
    await saveMood();
    setShowButton(false);
  };
  const handleUpdateMood = async () => {
    setIsFeedbackSubmitted(true);
    await updateMood();
    setShowButton(false);
  };

  const saveMood = async () => {
    try {
      const answer = await SurveyService.saveTrendSurveyAnswer(
        id,
        selectedMood.toUpperCase(),
        formatTextToString(feedback)
      );
      setLastAnswerId(answer.ID);
    } catch (error) {
      console.error('Ein Fehler ist beim Speichern der Antwort aufgetreten:', error);
    }
  };

  const updateMood = async () => {
    try {
      await SurveyService.updateTrendSurveyAnswer(
        lastAnswerId,
        id,
        selectedMood.toUpperCase(),
        formatTextToString(feedback)
      );
    } catch (error) {
      console.error('Ein Fehler ist beim Speichern der Antwort aufgetreten:', error);
    }
  };

  const renderIntro = () => {
    if (isFirstPage) {
      return (
        <Box>
          <Typography
            variant="h4"
            sx={{
              color: primaryColor,
              marginBottom: theme.spacing(5),
              fontWeight: 'bold',
              fontFamily: customFont,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: secondaryColor,
              pt: theme.spacing(5),
              mb: theme.spacing(3),
              pl: theme.spacing(3),
              fontFamily: customFont,
            }}
          >
            <Autolink text={introText} />
          </Typography>
        </Box>
      );
    }
  };

  const renderFeedbackIntro = () => {
    if (currentPage === 1 && title && negativeText && positiveText && neutralText) {
      return (
        <MoodBarometerIntro
          {...{
            primaryColor,
            secondaryColor,
            customFont,
            companyName,
            title,
            negativeText,
            positiveText,
            neutralText,
            question,
          }}
          onChange={setSelectedMood}
        />
      );
    }
  };

  const renderFeedbackComment = () => {
    if (
      currentPage === 2 &&
      feedbackActive &&
      !isFeedbackSubmitted &&
      feedbackText &&
      feedbackTitle &&
      title &&
      selectedMood !== 'positive'
    ) {
      return (
        <MoodBarometerComment
          {...{ secondaryColor, primaryColor, customFont, feedbackText, feedbackTitle, title }}
          onChange={setFeedback}
        />
      );
    }
  };

  const renderFeedbackOutro = () => {
    if ((currentPage === 2 && !feedbackActive) || isFeedbackSubmitted) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: primaryColor,
              marginBottom: isLastPage ? theme.spacing(4) : theme.spacing(3),
              fontWeight: 'bold',
              fontFamily: customFont,
            }}
          >
            <Autolink text={outroText} />
          </Typography>
          {contactEmail && selectedMood === 'negative' && !feedbackActive && (
            <Typography
              sx={{
                color: secondaryColor,
                fontWeight: 'bold',
                fontSize: '1.1rem',
                mt: 4,
                fontFamily: customFont,
              }}
            >
              {contactEmail ? `${t('appreciate_feedback')}: ${contactEmail}` : null}
            </Typography>
          )}
          {platform && selectedMood === 'positive' && (
            <Box
              sx={{
                color: primaryColor,
                marginBottom: isLastPage ? theme.spacing(-4) : theme.spacing(3),
                fontWeight: 'bold',
                fontFamily: customFont,
                mt: theme.spacing(2),
              }}
            ></Box>
          )}
          {platformUrl && selectedMood === 'positive' && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'fit-content',
                  margin: '0 auto',
                  mb: 4,
                }}
              >
                <Link
                  href={platformUrl}
                  target={'_blank'}
                  underline={'none'}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#fff',
                      backgroundColor: primaryColor,
                      padding: '.2em .5em',
                      fontWeight: 'light',
                      borderRadius: '4px',
                      boxShadow:
                        'rgba(0, 0, 0, 0.2) 0px 3px 1px -2px, rgba(0, 0, 0, 0.14) 0px 2px 2px 0px, rgba(0, 0, 0, 0.12) 0px 1px 5px 0px;',
                      mt: theme.spacing(4),
                      fontFamily: customFont,
                      '&:hover': {
                        opacity: 0.9,
                      },
                    }}
                  >
                    {`${t('please_rate_us')} ${SurveyUtils.getName(platformName)}!`}
                  </Typography>
                </Link>
                <Link
                  href={platformUrl}
                  variant="body1"
                  underline="none"
                  target="_blank"
                  sx={{
                    color: primaryColor,
                    fontWeight: 'bold',
                    fontFamily: customFont,
                  }}
                >
                  <Box
                    component={'img'}
                    sx={{
                      width: '100%',
                      maxWidth: '256px',
                      height: 'auto',
                      minHeight: '64px',
                      m: theme.spacing(2),
                    }}
                    src={constants.platform_logos[currentPlatform.toLowerCase()]}
                    alt={`${platform} Logo`}
                  />
                </Link>
              </Box>
            </>
          )}
        </Box>
      );
    }
  };

  const finishedAfterRating = !feedbackActive || selectedMood === 'positive';
  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        width: isFirstPage ? '57%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        alignSelf: 'center',
        ml: isSmallScreen ? '1%' : '10%',
        mr: isSmallScreen ? '1%' : '10%',
        mt: 'auto',
      }}
    >
      {renderIntro()}
      {renderFeedbackIntro()}
      {renderFeedbackOutro()}
      {renderFeedbackComment()}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          textAlign: 'center',
          mt: theme.spacing(4),
        }}
      >
        {!isLastPage && showButton && (
          <Button
            variant="contained"
            disabled={selectedMood === '' && currentPage === 1}
            sx={{
              backgroundColor: primaryColor,
              width: '120px',
              padding: theme.spacing(2),
            }}
            onClick={() => {
              handlePageChange(1);
              if (currentPage === 1) {
                if (finishedAfterRating) {
                  handleSaveMood();
                } else {
                  saveMood();
                }
              }
            }}
          >
            {currentPage === 1 && finishedAfterRating ? t('send') : t('next')}
          </Button>
        )}

        {isLastPage && !finishedAfterRating && !isFeedbackSubmitted && (
          <Button
            variant="contained"
            sx={{
              backgroundColor: primaryColor,
              width: '120px',
              padding: theme.spacing(2),
            }}
            onClick={handleUpdateMood}
          >
            {t('send')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default MoodBarometerContent;
